import {
  BookTextIcon,
  LandmarkIcon,
  ReceiptTextIcon,
  ScaleIcon,
  ScrollTextIcon,
  TrendingUpIcon,
  WalletIcon,
} from "lucide-react"

import { FinanceInvestigationKind } from "@ibada/shared-types/finance"

import { PeriodPreset } from "@/shared/lib/dates/period-range"

import {
  AccountingEndpoint,
  FinanceReportCategory,
  ReportDrilldownTargetKind,
  ReportExportKind,
  ReportFieldKind,
  StatementSection,
  type FinanceReportEntry,
  ReportModule,
} from "@/features/reports/model/report-registry-types"

const PERIOD_FIELD = {
  key: "period",
  kind: ReportFieldKind.Period,
  labelKey: "finance.reports.fields.period",
  defaultPreset: PeriodPreset.MonthToDate,
} as const

/**
 * The five accounting endpoints, presented as seven catalog reports (the single /statements payload is
 * carved into Balance Sheet, Income Statement, Cash Flow). Every report exports a client-side CSV — the
 * statements via `adaptStatement`, which flattens their grouped sections into line/amount rows. Print stays as
 * a formatted fallback.
 */
export const ACCOUNTING_ENTRIES: readonly FinanceReportEntry[] = [
  {
    id: "balance-sheet",
    module: ReportModule.Finance,
    category: FinanceReportCategory.FinancialStatements,
    icon: ScaleIcon,
    titleKey: "finance.reports.entries.balanceSheet.title",
    descriptionKey: "finance.reports.entries.balanceSheet.description",
    keywords: ["balance", "assets", "liabilities", "net assets", "position"],
    configSchema: { fields: [{ ...PERIOD_FIELD }] },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.Statements, section: StatementSection.BalanceSheet },
    viewer: { layout: "custom", customRenderer: "statements" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "income-statement",
    module: ReportModule.Finance,
    category: FinanceReportCategory.FinancialStatements,
    icon: TrendingUpIcon,
    titleKey: "finance.reports.entries.incomeStatement.title",
    descriptionKey: "finance.reports.entries.incomeStatement.description",
    keywords: ["income", "expenses", "surplus", "profit", "loss", "revenue"],
    configSchema: { fields: [{ ...PERIOD_FIELD }] },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.Statements, section: StatementSection.IncomeStatement },
    viewer: { layout: "custom", customRenderer: "statements" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "cash-flow",
    module: ReportModule.Finance,
    category: FinanceReportCategory.FinancialStatements,
    icon: WalletIcon,
    titleKey: "finance.reports.entries.cashFlow.title",
    descriptionKey: "finance.reports.entries.cashFlow.description",
    keywords: ["cash", "flow", "inflow", "outflow", "liquidity"],
    configSchema: { fields: [{ ...PERIOD_FIELD }] },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.Statements, section: StatementSection.CashFlow },
    viewer: { layout: "custom", customRenderer: "statements" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "trial-balance",
    module: ReportModule.Finance,
    category: FinanceReportCategory.FinancialStatements,
    icon: LandmarkIcon,
    titleKey: "finance.reports.entries.trialBalance.title",
    descriptionKey: "finance.reports.entries.trialBalance.description",
    keywords: ["trial", "balance", "debit", "credit", "ledger"],
    configSchema: { fields: [] },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.TrialBalance },
    drilldown: {
      kind: ReportDrilldownTargetKind.Report,
      column: "balance",
      reportId: "general-ledger",
      params: { accountingAccountId: "accountingAccountId" },
    },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "general-ledger",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Transactions,
    icon: BookTextIcon,
    titleKey: "finance.reports.entries.generalLedger.title",
    descriptionKey: "finance.reports.entries.generalLedger.description",
    keywords: ["ledger", "account transactions", "running balance", "entries"],
    configSchema: {
      fields: [
        { key: "accountingAccountId", kind: ReportFieldKind.Account, labelKey: "finance.reports.fields.account", required: true },
        { ...PERIOD_FIELD },
      ],
    },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.Ledger },
    // Each line names a transaction; clicking it opens that transaction's audit — who recorded it, when, and
    // what changed since. Lines with no transaction behind them (an opening balance) carry no id, so they
    // render as plain text instead of a dead link.
    drilldown: {
      kind: ReportDrilldownTargetKind.Route,
      column: "reference",
      path: `/finance/investigations/investigation/${FinanceInvestigationKind.Transaction}/:transactionId`,
      params: { transactionId: "transactionId" },
    },
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "journal-entries",
    module: ReportModule.Finance,
    category: FinanceReportCategory.Transactions,
    icon: ScrollTextIcon,
    titleKey: "finance.reports.entries.journal.title",
    descriptionKey: "finance.reports.entries.journal.description",
    keywords: ["journal", "double entry", "debit", "credit", "postings"],
    configSchema: { fields: [] },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.Journal },
    viewer: { layout: "custom", customRenderer: "journal" },
    export: { kind: ReportExportKind.ClientCsv },
  },
  {
    id: "chart-of-accounts",
    module: ReportModule.Finance,
    category: FinanceReportCategory.FinancialStatements,
    icon: ReceiptTextIcon,
    titleKey: "finance.reports.entries.chartOfAccounts.title",
    descriptionKey: "finance.reports.entries.chartOfAccounts.description",
    keywords: ["chart of accounts", "coa", "accounts", "ledger accounts"],
    configSchema: { fields: [] },
    dataSource: { kind: "accounting", endpoint: AccountingEndpoint.Chart },
    viewer: { layout: "result-panel", defaultView: "table" },
    // Every balance here is a claim; the general ledger is the proof. Clicking one opens the journal lines
    // that sum to it.
    drilldown: {
      kind: ReportDrilldownTargetKind.Report,
      column: "balance",
      reportId: "general-ledger",
      params: { accountingAccountId: "accountingAccountId" },
    },
    export: { kind: ReportExportKind.ClientCsv },
  },
]
